import { Button, Input } from '@axioma/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Scale, X } from 'lucide-react';
import { useState } from 'react';

import { api, extractErrorMessage } from '../lib/api.js';

const UF_OPTIONS = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
];

export function OabSetupModal({
  initialNumber,
  initialState,
  onClose,
}: {
  initialNumber?: string | null;
  initialState?: string | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [oabNumber, setOabNumber] = useState(initialNumber ?? '');
  const [oabState, setOabState] = useState(initialState ?? 'SC');

  const save = useMutation({
    mutationFn: async () => {
      const { data } = await api.put<{ ok: boolean; oabNumber: string; oabState: string }>(
        '/auth/me/oab',
        { oabNumber: oabNumber.replace(/\D/g, ''), oabState },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-ax-modal flex items-start justify-center bg-black/60 px-4 py-12 backdrop-blur-sm">
      <div className="shadow-ax-xl w-full max-w-md rounded-ax-lg border border-ax-line bg-ax-graphite">
        <div className="flex items-center justify-between border-b border-ax-line px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-ax-sm border border-[rgba(229,165,71,0.40)] bg-[rgba(229,165,71,0.08)] text-ax-aurora">
              <Scale size={16} />
            </div>
            <div>
              <p className="ax-eyebrow text-ax-text-tertiary">Vigília · DJEN</p>
              <h2 className="font-display text-lg tracking-tight text-ax-text-primary">
                Cadastrar OAB
              </h2>
            </div>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-ax-sm p-1 text-ax-text-tertiary hover:bg-ax-night hover:text-ax-text-primary"
          >
            <X size={18} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-5 px-6 py-5"
        >
          <p className="ax-body-sm text-ax-text-secondary">
            Sua OAB vai liberar a captura automática de TODAS as intimações publicadas no Diário de
            Justiça Eletrônico Nacional (DJEN/CNJ) — texto integral, indexado nominalmente.
          </p>

          <div className="grid grid-cols-[1fr_120px] gap-3">
            <Input
              label="Número da OAB"
              placeholder="123456"
              mono
              value={oabNumber}
              onChange={(e) => setOabNumber(e.target.value.replace(/\D/g, ''))}
              required
              maxLength={10}
            />
            <div>
              <label className="ax-eyebrow mb-1 block text-ax-text-tertiary">UF</label>
              <select
                value={oabState}
                onChange={(e) => setOabState(e.target.value)}
                className="w-full rounded-ax-sm border border-ax-line bg-ax-night px-2 py-2 font-mono text-sm text-ax-text-primary focus:outline-none focus:ring-1 focus:ring-ax-aurora"
              >
                {UF_OPTIONS.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {save.isError && (
            <p className="text-xs text-ax-critical">{extractErrorMessage(save.error)}</p>
          )}

          <Button
            type="submit"
            variant="signature"
            size="lg"
            loading={save.isPending}
            disabled={oabNumber.length < 3}
            className="w-full"
          >
            Salvar e ativar DJEN
          </Button>
        </form>
      </div>
    </div>
  );
}
